import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { LockKeyhole, Lock, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { formatApiErrorDetail } from '../../auth';
import { AdminGames } from './AdminGames';
import { OriginalsAccessContext as Access } from './originalsAccessContext';
import { useAdminAccessPolicy } from './useAdminAccessPolicy';
import { AdminAccessStatus } from './AdminAccessStatus';
import './adminAccess.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/originals/unlock`;

export const AdminOriginalsGate = ({ active, actionsTarget }) => {
  const policy = useAdminAccessPolicy(active);
  if (!active) return null;
  if (policy.required === null) return <AdminAccessStatus policy={policy} testid="admin-originals-access" />;
  if (policy.required) return <MasterProtectedOriginalsGate active={active} actionsTarget={actionsTarget} />;
  const headers = { Authorization: `Bearer ${localStorage.getItem('gv_token')}` };
  const handleFailure = (failure) => { if ([401, 403].includes(failure.response?.status)) policy.reload(); };
  return <Access.Provider value={{ headers, handleFailure }}><div className="admin-originals-workspace" data-testid="admin-originals-workspace"><AdminGames /></div></Access.Provider>;
};

const MasterProtectedOriginalsGate = ({ active, actionsTarget }) => {
  const [grant, setGrant] = useState(null), [password, setPassword] = useState('');
  const [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const grantRef = useRef(null), mounted = useRef(true);
  const revoke = (current) => {
    if (current) axios.delete(API, { headers: { Authorization: `Bearer ${current.session}`, 'X-Admin-Step-Up': current.token } }).catch(() => {});
  };
  const lock = useCallback(() => { revoke(grantRef.current); grantRef.current = null; setGrant(null); setPassword(''); }, []);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; revoke(grantRef.current); }; }, []);
  useEffect(() => {
    if (!grant) return;
    const timer = setTimeout(lock, Math.max(0, grant.until - Date.now()));
    const check = () => { if (Date.now() >= grant.until) lock(); };
    window.addEventListener('focus', check);
    return () => { clearTimeout(timer); window.removeEventListener('focus', check); };
  }, [grant, lock]);
  useEffect(() => { if (!active) { setPassword(''); setError(''); } }, [active]);
  const unlock = async (event) => {
    event.preventDefault(); if (busy) return;
    setBusy(true); setError('');
    const session = localStorage.getItem('gv_token');
    try {
      const { data } = await axios.post(API, { master_password: password }, { headers: { Authorization: `Bearer ${session}` } });
      const next = { token: data.token, until: Date.now() + data.expires_in * 1000, session };
      if (!mounted.current) { revoke(next); return; }
      grantRef.current = next; setGrant(next);
    } catch (failure) { if (mounted.current) setError(formatApiErrorDetail(failure.response?.data?.detail || 'Unable to unlock Originals.')); }
    finally { if (mounted.current) { setPassword(''); setBusy(false); } }
  };
  if (!active) return null;
  if (!grant || Date.now() >= grant.until) return <section className="admin-originals-lock" data-testid="admin-originals-locked">
    <LockKeyhole size={28} aria-hidden="true" /><h2 data-testid="admin-originals-lock-title">Unlock Originals</h2>
    <p data-testid="admin-originals-lock-note">Master password required · 15-minute access</p>
    <form onSubmit={unlock}><label htmlFor="admin-originals-master">Master password</label><Input id="admin-originals-master" data-testid="admin-originals-master" type="password" autoComplete="off" value={password} onChange={(event) => setPassword(event.target.value)} required disabled={busy} maxLength={128} />
      {error && <p role="alert" className="keno-error" data-testid="admin-originals-unlock-error">{error}</p>}
      <Button data-testid="admin-originals-unlock" type="submit" disabled={busy || !password}><ShieldCheck size={17} />{busy ? 'Verifying…' : 'Unlock Originals'}</Button>
    </form>
  </section>;
  const headers = { Authorization: `Bearer ${grant.session}`, 'X-Admin-Step-Up': grant.token };
  const handleFailure = (failure) => { if (failure.response?.status === 401 || failure.response?.data?.detail?.code === 'originals_locked') lock(); };
  const actions = <div className="admin-originals-access-bar"><span data-testid="admin-originals-unlocked"><ShieldCheck size={14} aria-hidden="true" />Unlocked</span><Button variant="ghost" data-testid="admin-originals-lock" onClick={lock}><Lock size={15} />Lock</Button></div>;
  return <Access.Provider value={{ headers, handleFailure }}>{actionsTarget && createPortal(actions, actionsTarget)}<div className="admin-originals-workspace" data-testid="admin-originals-workspace"><AdminGames actions={!actionsTarget && actions} /></div></Access.Provider>;
};